import React, { useRef, useState } from 'react';
import { Upload, Loader2, Image as ImageIcon } from 'lucide-react';
import { AdminAPI, absoluteUrl } from '../../lib/api';
import { useToast } from '@/hooks/use-toast';

// Uploads to object storage and returns an absolute URL via onChange.
const ImageUploader = ({ value, onChange, label = 'Image', testId = 'image-uploader' }) => {
  const { toast } = useToast();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please choose an image file.' });
      return;
    }
    setUploading(true);
    try {
      const res = await AdminAPI.upload(file);
      onChange(absoluteUrl(res.url));
      toast({ title: 'Image uploaded', description: file.name });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1.5">{label}</p>
      <div className="flex items-center gap-4">
        <div className="h-20 w-20 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
          {value ? (
            <img src={value} alt="preview" className="h-full w-full object-contain" data-testid={`${testId}-preview`} />
          ) : (
            <ImageIcon className="h-6 w-6 text-slate-400" />
          )}
        </div>
        <div className="flex-1">
          <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid={`${testId}-input`} />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-[#0A2A63] text-[#0A2A63] hover:bg-[#0A2A63] hover:text-white font-semibold text-sm btn-transition"
            data-testid={`${testId}-btn`}
          >
            {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</> : <><Upload className="h-4 w-4" /> Upload image</>}
          </button>
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="...or paste an image URL"
            className="mt-2 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
            data-testid={`${testId}-url`}
          />
        </div>
      </div>
    </div>
  );
};

export default ImageUploader;
